import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Trophy, Target, Star, Calendar, MapPin, Clock,
  Users, TrendingUp, LogOut, User, Award
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlayerDashboard({ user, logout }) {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/academy/player/dashboard`, {
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const dayNames = {
    1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday",
    5: "Friday", 6: "Saturday", 7: "Sunday"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const { player, stats, training_sessions, upcoming_games, upcoming_events } = dashboardData || {};

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{player?.name || "Player"}</h1>
                <p className="text-emerald-200">
                  {player?.membership_number} • {player?.team || "No Team Assigned"}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="text-white border-white/50 hover:bg-white/20"
              onClick={logout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 text-center">
              <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{stats?.goals || 0}</p>
              <p className="text-sm text-slate-400">Goals</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 text-center">
              <Target className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{stats?.assists || 0}</p>
              <p className="text-sm text-slate-400">Assists</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 text-center">
              <Award className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{stats?.motm_awards || 0}</p>
              <p className="text-sm text-slate-400">MOTM Awards</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 text-center">
              <TrendingUp className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{stats?.attendance_rate || 0}%</p>
              <p className="text-sm text-slate-400">Attendance</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Training Schedule */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-400" />
                My Training Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {training_sessions?.length > 0 ? (
                <div className="space-y-3">
                  {training_sessions.map((session, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                    >
                      <p className="font-medium text-white">{session.name}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {dayNames[session.day] || session.day}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {session.time}
                        </span>
                      </div>
                      {session.location && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {session.location}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">No training sessions enrolled</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Games */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Upcoming Games
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcoming_games?.length > 0 ? (
                <div className="space-y-3">
                  {upcoming_games.map((game, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-white">vs {game.opponent}</p>
                        <Badge className={game.is_home ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-500/20 text-blue-400"}>
                          {game.is_home ? "Home" : "Away"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(game.date).toLocaleDateString()}
                        </span>
                        {game.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {game.time}
                          </span>
                        )}
                      </div>
                      {game.location && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {game.location}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">No upcoming games scheduled</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Events */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-purple-400" />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcoming_events?.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-3">
                {upcoming_events.map((event, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                  >
                    <p className="font-medium text-white">{event.title}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(event.date).toLocaleDateString()}
                      </span>
                    </div>
                    {event.location && (
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">No upcoming events</p>
            )}
          </CardContent>
        </Card>

        {/* Footer note */}
        <p className="text-center text-slate-500 text-sm">
          This is your player dashboard. For payment info or registration changes, ask your parent to log in.
        </p>
      </div>
    </div>
  );
}
